/* components/PaginationInfiniteScroll
- Used within collection and search templates

Example
<pagination-infinite-scroll
    pagination="[data-ref='pagination-page']",
    list="[data-ref='pagination-list']",
    method="scroll"
    offset="0"
    pageSize="{{ paginate.page_size | escape }}"
    totalItems="{{ paginate.items | escape }}"
    infiniteScroll="true"
>
    [[pagination infinite scroll content]]
</pagination-infinite-scroll>
*/

import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';

@Component
export class PaginationInfiniteScroll extends ThemeComponent {
    addListeners: (() => void)|null = null;
    addManualListener: (() => void)|null = null;
    resizeListener: (() => void)|null = null;

    @Prop({ default: '[data-ref="pagination-page"]'}) pagination!: string;
    @Prop({ default: '[data-ref="pagination-list"]'}) list!: string;
    @Prop({ default: false}) method!: string;
    @Prop({ default: false}) offset!: number;
    @Prop({ default: 'Loading...'}) loadingText!: string;
    @Prop({ default: 0}) totalItems!: number;
    @Prop({ default: 0}) pageSize!: number;
    @Prop({ default: false}) infiniteScroll!: boolean;
    @Ref({ required: false }) loadMore!: HTMLButtonElement;

    private clickActive = false;
    private nextPageLinkEl: HTMLAnchorElement|null = null;
    private nextPageUrl: string | null = '';
    private listEl : HTMLElement|null = null;
    private paginationEl : HTMLElement|null = null;

    init() {
        this.listEl = document.querySelector(this.list);
        this.paginationEl = document.querySelector(this.pagination);

        if (this.listEl && this.paginationEl) {
            this.nextPageLinkEl = this.paginationEl.querySelector('a');
            this.clickActive = true;

            if (this.infiniteScroll && this.nextPageLinkEl !== null) {
                this.nextPageLinkEl.addEventListener('click', this.preventNextPagedClick);
            }

            if (this.loadMore) {
                this.loadMore.addEventListener('click', (e) => this.loadNextPageManually(e));
            }

            if (this.infiniteScroll && !this.addListeners) {
                this.addListeners = () => this.checkEndOfPage();
            }

            if (this.infiniteScroll && this.addListeners) {
                window.addEventListener('resize', this.addListeners);
                window.addEventListener('scroll', this.addListeners);
                window.addEventListener('orientationchange', this.addListeners);
            }
        }
    }

    loadNextPageManually(event: Event) {
        event.preventDefault();

        if (this.paginationEl) {
            this.nextPageLinkEl = this.paginationEl.querySelector('a');

            if (this.nextPageLinkEl) {
                this.nextPageLinkEl.innerHTML = this.loadingText;
                this.nextPageUrl = this.nextPageLinkEl.href;
                this.loadNextPage();
            }
        }
    }

    checkEndOfPage() {
        if (this.paginationEl) {
            const top = this.paginationEl.getBoundingClientRect().top - this.offset;
            const bottom = this.paginationEl.getBoundingClientRect().bottom + this.offset;

            if (top <= window.innerHeight && bottom >= 0) {
                this.nextPageLinkEl = this.paginationEl.querySelector('a');

                if (this.nextPageLinkEl) {
                    this.nextPageLinkEl.innerHTML = this.loadingText;
                    this.nextPageUrl = this.nextPageLinkEl.href;
                    this.loadNextPage();
                }
            }
        }
    }

    preventNextPagedClick(event: Event) {
        event.preventDefault();

        if (this.clickActive) {
            if (this.nextPageLinkEl) {
                this.nextPageLinkEl.innerHTML = this.loadingText;

                if (this.nextPageUrl) {
                    this.nextPageUrl = this.nextPageLinkEl.href;
                }
            }

            this.clickActive = false;
            this.loadNextPage();
        }
    }

    loadNextPage() {
        if (!this.nextPageUrl) { return; }

        fetch(this.nextPageUrl, {
            method: 'get'
        })
            .then((response) => response.text())
            .then((text) => {
                const parser = new DOMParser();
                const htmlDocument = parser.parseFromString(text, "text/html");
                const newList = htmlDocument.querySelectorAll(this.list)[0];
                const newPagination = htmlDocument.querySelectorAll(this.pagination)[0];

                if (this.listEl) {
                    this.listEl.insertAdjacentHTML('beforeend', newList.innerHTML);
                }

                if (this.paginationEl) {
                    this.paginationEl.innerHTML = newPagination.innerHTML;
                }

                if (this.listEl && this.paginationEl) {
                    this.renderImages();
                    this.destroy();
                    this.init();
                }

            }).catch((event: unknown) => {
                if (event instanceof Error) {
                    this.$log.error(event.message);
                }
            });
    }

    renderImages() {
        const images = document.querySelectorAll('img[data-preload]');
        for (const image of images) {
            if (image.getAttribute('data-preload') !== 'loaded') {
                const src = image.getAttribute('data-preload') ?
                    image.getAttribute('data-preload') :
                    image.getAttribute('src');
                if (src) {
                    image.setAttribute('src', src);
                    image.removeAttribute('data-preload');
                }
            }
        }
    }

    destroy() {
        if (this.infiniteScroll) {
            if (this.addListeners) {
                window.removeEventListener('resize', this.addListeners);
                window.removeEventListener('scroll', this.addListeners);
                window.removeEventListener('orientationchange', this.addListeners);
            }

            if (this.nextPageLinkEl) {
                this.nextPageLinkEl.removeEventListener('click', this.preventNextPagedClick);
            }
        }

        this.loadMore.removeEventListener('click', this.loadNextPageManually);
    }
}
